import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { ReportProblemOutlined as GrievanceIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';
const TEAL = '#0d9488';

function statusColor(status) {
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'in_progress') return 'info';
  return 'warning';
}

function statusLabel(status) {
  const map = {
    open: 'Open',
    in_progress: 'In progress',
    resolved: 'Resolved',
    closed: 'Closed'
  };
  return map[status] || status;
}

function targetLabel(row) {
  if (row.target_type === 'hr') return 'HR / Company';
  return row.manager_name ? `Manager (${row.manager_name})` : 'Manager';
}

export default function StaffingGrievancePanel({ token, embedded = false }) {
  const [meta, setMeta] = useState({ has_manager: false, manager_name: null });
  const [list, setList] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [form, setForm] = useState({ target_type: 'hr', subject: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const load = useCallback(async () => {
    if (!API_URL || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/candidate/staffing/grievances`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load grievances');
      setList(data.grievances || []);
      setMeta({ has_manager: !!data.has_manager, manager_name: data.manager_name || null });
    } catch (e) {
      setFeedback(e.message);
      setList([]);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setFeedback('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/staffing/grievances`, {
        method: 'POST',
        headers,
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit');
      setFeedback(data.message || 'Grievance submitted.');
      setForm({ target_type: 'hr', subject: '', description: '' });
      await load();
    } catch (e) {
      setFeedback(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inner = (
    <Stack spacing={2}>
      {!embedded && (
        <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GrievanceIcon color="warning" /> Grievance center
        </Typography>
      )}
      <Alert severity="info" variant="outlined">
        Raise a confidential grievance against <strong>HR</strong>
        {meta.has_manager ? (
          <>
            {' '}
            or your reporting manager (<strong>{meta.manager_name}</strong>)
          </>
        ) : (
          '. Assign a manager in HR settings to raise grievances against your manager.'
        )}
        . Your employer will review and respond here.
      </Alert>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography fontWeight={700} gutterBottom>
            New grievance
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              select
              size="small"
              fullWidth
              label="Raise against"
              value={form.target_type}
              onChange={(e) => setForm((f) => ({ ...f, target_type: e.target.value }))}
            >
              <MenuItem value="hr">HR / Company</MenuItem>
              <MenuItem value="manager" disabled={!meta.has_manager}>
                Reporting manager{meta.manager_name ? ` (${meta.manager_name})` : ''}
              </MenuItem>
            </TextField>
            <TextField
              size="small"
              fullWidth
              label="Subject"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="Brief summary"
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={4}
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Describe the issue in detail"
            />
            <Button
              variant="contained"
              disabled={submitting}
              onClick={submit}
              sx={{ alignSelf: 'flex-start', bgcolor: TEAL }}
            >
              {submitting ? 'Submitting…' : 'Submit grievance'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {feedback && (
        <Alert severity={feedback.includes('submitted') ? 'success' : 'warning'} onClose={() => setFeedback('')}>
          {feedback}
        </Alert>
      )}

      <Typography fontWeight={700}>Your grievances</Typography>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No grievances submitted yet.
        </Typography>
      ) : (
        list.map((g) => (
          <Card key={g.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap spacing={1}>
                <Box>
                  <Typography fontWeight={700}>{g.subject}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Against {targetLabel(g)} · {g.created_at ? new Date(g.created_at).toLocaleString() : ''}
                  </Typography>
                </Box>
                <Chip size="small" label={statusLabel(g.status)} color={statusColor(g.status)} />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {g.description}
              </Typography>
              {g.company_response && (
                <Alert severity="success" variant="outlined" sx={{ mt: 1.5, bgcolor: '#f0fdf4' }}>
                  <Typography variant="caption" fontWeight={700}>
                    HR response
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {g.company_response}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </Stack>
  );

  if (embedded) return inner;
  return <Box>{inner}</Box>;
}
